import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Phone, MessageSquare } from "lucide-react";
import { business, images } from "@/data/site";
import { MaskLines } from "./motion";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] flex flex-col lg:items-end lg:justify-end overflow-hidden bg-[#1C1C1C]"
    >
      {/* Mobile & tablet: image band on top so both crew members' faces stay fully visible */}
      <div className="lg:hidden relative w-full h-[46svh] shrink-0">
        <img
          src={images.hero}
          alt="Two movers carrying boxes from a moving truck, Duke Summers Moving"
          loading="eager"
          className="h-full w-full object-cover object-[50%_28%]"
        />
        <div className="absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-[#1C1C1C]/70 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#1C1C1C] to-transparent" />
      </div>

      {/* Desktop: full-bleed background image, anchored right, dark gradient on the left */}
      <motion.div style={{ y, scale }} className="hidden lg:block absolute inset-0 z-0">
        <div className="absolute inset-y-0 right-0 w-[52%]">
          <img
            src={images.hero}
            alt="Two movers carrying boxes from a moving truck, Duke Summers Moving"
            loading="eager"
            className="h-full w-full object-cover object-center"
          />
        </div>
        {/* Dark panel covers only the left ~45% behind the text; both men stay clear on the right */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#1C1C1C] from-46% via-[#1C1C1C]/45 via-52% to-transparent to-58%" />
        {/* Gentle bottom gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#1C1C1C]/70 via-transparent to-transparent" />
      </motion.div>

      <div className="relative z-10 w-full mx-auto max-w-7xl px-5 sm:px-8 pt-10 pb-14 lg:pb-16 lg:pt-32">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.8 }}
          className="mb-6"
        >
          <span className="inline-block bg-[#1C1C1C]/90 backdrop-blur-sm text-[#FF4500] font-bold text-sm tracking-[0.25em] uppercase px-4 py-2">
            Local Moving Help
          </span>
          <span className="block text-neutral-100 font-semibold text-sm tracking-[0.25em] uppercase mt-3 [text-shadow:0_1px_8px_rgba(0,0,0,0.6)]">
            Dallas–Fort Worth · Memphis · and Beyond
          </span>
        </motion.div>

        <h1 className="font-display font-extrabold uppercase leading-[0.98] tracking-tight text-[1.7rem] sm:text-6xl lg:text-3xl max-w-5xl lg:max-w-2xl">
          <MaskLines
            lines={["A Name You Can Trust", "With Everything", "You Own."]}
            start={0.15}
          />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.8 }}
          className="mt-8 max-w-2xl lg:max-w-md text-base sm:text-lg text-neutral-300 leading-relaxed"
        >
          Professional moving labor for small local moves, apartment
          relocations, furniture moves, and loading and unloading. You provide
          the truck — we provide the muscle.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.8 }}
          className="mt-10 flex flex-col gap-4"
        >
          <a
            href="#quote"
            data-testid="hero-quote-btn"
            className="group inline-flex items-center justify-center gap-2 bg-[#FF4500] text-[#1C1C1C] font-bold px-8 py-4 text-base hover:bg-[#E03C00] transition-colors"
          >
            Request a Quote
            <ArrowUpRight
              className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              strokeWidth={2}
            />
          </a>
          <div className="grid grid-cols-2 gap-4">
            <a
              href="tel:+19729476017"
              data-testid="hero-call-now-btn"
              className="inline-flex items-center justify-center gap-2 border border-white/25 text-white font-semibold px-4 sm:px-8 py-4 text-base whitespace-nowrap hover:bg-white hover:text-[#1C1C1C] transition-colors"
            >
              <Phone className="h-5 w-5" strokeWidth={1.75} />
              Call Now
            </a>
            <a
              href="sms:+19729476017?&body=Hi%2C%20I%27d%20like%20a%20moving%20quote."
              data-testid="hero-text-us-btn"
              className="inline-flex items-center justify-center gap-2 border border-white/25 text-white font-semibold px-4 sm:px-8 py-4 text-base whitespace-nowrap hover:bg-white hover:text-[#1C1C1C] transition-colors"
            >
              <MessageSquare className="h-5 w-5" strokeWidth={1.75} />
              Text Us
            </a>
          </div>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.0, duration: 0.8 }}
          data-testid="hero-pricing"
          className="mt-5 text-sm sm:text-base font-semibold text-white"
        >
          Starting at <span className="text-[#FF4500]">$50 per mover, per hour</span>. 2-hour minimum. $25 booking fee per job.
        </motion.p>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-2 text-xs text-neutral-500 max-w-xl lg:max-w-md"
        >
          Duke Summers Moving is a moving labor service provider only. We do not
          transport household goods and are not a licensed motor carrier.
        </motion.p>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.15, duration: 0.8 }}
          className="mt-6 text-sm text-neutral-400 max-w-xl lg:max-w-md"
        >
          Built for quick, local moves.
          <span className="text-neutral-200 font-medium">
            {" "}
            {business.secondaryTaglines[0]}
          </span>
        </motion.p>
      </div>
    </section>
  );
};
